import type { IDataSource, IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import type { ServiceType } from "@/data/model/Enums";
import type { IRoom } from "@/data/model/Room";
import type { IConfigSchemaRow } from "@/data/model/Shared";
import type { IRoomsQry } from "@/data/queries/room/RoomsQry";

import { logError } from "@/shared/logging/errors";

import { Factory } from "./Factory";

export class RequestCache {
    private static rooms?: IPaginatedResult<IRoom>;
    private static availableServices?: Array<{ location: string; services: Array<ServiceType> }>;
    private static serviceSchema?: Array<{ schema: Array<IConfigSchemaRow>; service: ServiceType }>;

    public static roomDataSource(): IDataSource<IRoom> {
        return {
            getData: (f: IRoomsQry) => {
                return new Promise((resolve) => {
                    if (RequestCache.rooms !== undefined) {
                        resolve(RequestCache.rooms);
                        return;
                    }

                    return Factory.getInstance<RoomDao>(RoomDao)
                        .getData(f)
                        .then((s) => {
                            RequestCache.rooms = s;
                            resolve(RequestCache.rooms);
                        });
                });
            },
        };
    }

    public static getSchemaForService(service: ServiceType, locationId: string): Promise<Array<IConfigSchemaRow>> {
        return new Promise((resolve, reject) => {
            if (RequestCache.serviceSchema === undefined)
                RequestCache.serviceSchema = new Array<{
                    schema: Array<IConfigSchemaRow>;
                    service: ServiceType;
                }>();

            const existing = RequestCache.serviceSchema.find((c) => c.service === service);
            if (existing !== undefined) {
                resolve(existing.schema);
                return;
            }

            return Factory.getInstance<LocationDao>(LocationDao)
                .getServiceConfigSchema(locationId, service)
                .then((a) => {
                    RequestCache.serviceSchema!.push({
                        service: service,
                        schema: a,
                    });

                    resolve(a);
                })
                .catch((ex) => {
                    logError(ex);
                    reject();
                });
        });
    }

    public static getAvailableServices(locationId: string): Promise<Array<ServiceType>> {
        return new Promise((resolve, reject) => {
            if (RequestCache.availableServices === undefined)
                RequestCache.availableServices = new Array<{ location: string; services: Array<ServiceType> }>();

            const existing = RequestCache.availableServices.find((c) => c.location === locationId);
            if (existing !== undefined) {
                resolve(existing.services);
                return;
            }

            return Factory.getInstance<LocationDao>(LocationDao)
                .getServiceAvailability(locationId)
                .then((a) => {
                    RequestCache.availableServices!.push({
                        services: a,
                        location: locationId,
                    });

                    resolve(a);
                })
                .catch((ex) => {
                    logError(ex);
                    reject();
                });
        });
    }

    public static release() {
        RequestCache.rooms = undefined;
        RequestCache.serviceSchema = undefined;
        RequestCache.availableServices = undefined;
    }
}
